import { useState, useRef } from 'react';
import {
  Upload, Mail, FileText, Brain, CheckCircle, ArrowRight, Plus, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ZeoChat from '@/components/ZeoChat';
import { StatusEvent } from '@/components/ZeoChat';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const EVENT_ICONS = {
  vorgang_erstellt: <FileText className="w-3 h-3 text-blue-400" />,
  bescheid_verknuepft: <FileText className="w-3 h-3 text-emerald-400" />,
  betrag_aktualisiert: <ArrowRight className="w-3 h-3 text-amber-400" />,
  aufgabe_erledigt: <CheckCircle className="w-3 h-3 text-green-400" />,
  weiterbewilligung_aufgabe: <Brain className="w-3 h-3 text-rose-400" />,
  aufgabe_erstellt: <CheckCircle className="w-3 h-3 text-emerald-400" />,
  frist_erstellt: <FileText className="w-3 h-3 text-orange-400" />,
};

export default function KiPosterfassungPage() {
  const [files, setFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [events, setEvents] = useState([]);
  const [zeoText, setZeoText] = useState('');
  const [error, setError] = useState(null);
  const [fertig, setFertig] = useState(false);
  const [ergebnisse, setErgebnisse] = useState([]);
  const fileRef = useRef();
  const navigate = useNavigate();

  const token = localStorage.getItem('olympus_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  function handleFiles(fileList) {
    const arr = Array.from(fileList);
    setFiles(prev => [...prev, ...arr]);
    toast.success(`${arr.length} Dokument(e) hinzugefuegt`);
  }

  function removeFile(index) {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }

  async function startAnalyse() {
    if (!files.length) return;
    setIsAnalysing(true);
    setEvents([]);
    setZeoText('');
    setError(null);
    setFertig(false);
    setErgebnisse([]);

    const allErgebnisse = [];

    for (let i = 0; i < files.length; i++) {
      setCurrentFileIndex(i);
      if (i > 0) {
        setZeoText('');
        setEvents([]);
      }

      try {
        const formData = new FormData();
        formData.append('datei', files[i]);

        const endpoint = files.length === 1
          ? `${API}/api/posterfassung/analyse`
          : `${API}/api/posterfassung/analyse`;

        const response = await fetch(endpoint, {
          method: 'POST', headers, body: formData
        });

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let currentZeo = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'zeo_token') {
                currentZeo += data.token;
                setZeoText(currentZeo);
              } else if (data.type === 'error') {
                setError(data.message);
              } else if (data.type === 'fertig') {
                allErgebnisse.push({
                  datei: files[i].name,
                  analyse: data.analyse,
                });
              } else {
                setEvents(prev => [...prev, data]);
              }
            } catch (e) { /* ignore */ }
          }
        }
      } catch (e) {
        setError(`Fehler bei ${files[i].name}: ${e.message}`);
      }
    }

    setErgebnisse(allErgebnisse);
    setFertig(true);
    setIsAnalysing(false);
    toast.success(`${files.length} Dokument(e) verarbeitet`);
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="posterfassung-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-100">KI-Posterfassung</h1>
        <p className="text-sm text-slate-400 mt-1">
          Zeo analysiert eingehende Post, ordnet sie dem Klienten zu und erstellt Aufgaben
        </p>
      </div>

      {!isAnalysing && !fertig && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-slate-200 flex items-center gap-2">
              <Mail className="w-4 h-4 text-violet-400" />
              Eingehende Post
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              data-testid="post-dropzone"
              className="border-2 border-dashed border-slate-700 rounded-xl p-6 text-center cursor-pointer hover:border-violet-500/50 hover:bg-slate-800/30 transition-all"
              onClick={() => fileRef.current?.click()}
              onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
              onDragOver={e => e.preventDefault()}
            >
              <input
                ref={fileRef}
                type="file"
                multiple
                className="hidden"
                onChange={e => handleFiles(e.target.files)}
              />
              <Upload className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <div className="text-slate-400 text-sm">Dokumente hier ablegen oder klicken</div>
            </div>

            {files.length > 0 && (
              <div className="space-y-2">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 bg-slate-800/50 rounded-lg px-3 py-2">
                    <FileText className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-300 flex-1 truncate">{f.name}</span>
                    <span className="text-xs text-slate-500">{(f.size / 1024).toFixed(0)} KB</span>
                    <button onClick={() => removeFile(i)} className="text-slate-500 hover:text-red-400">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}

                <Button
                  data-testid="start-posterfassung-btn"
                  onClick={startAnalyse}
                  className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white h-11"
                >
                  <Brain className="w-4 h-4 mr-2" />
                  {files.length} Dokument{files.length > 1 ? 'e' : ''} analysieren
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Zeo Chat */}
      {(isAnalysing || fertig || error) && (
        <>
          {isAnalysing && files.length > 1 && (
            <div className="text-sm text-slate-400 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Dokument {currentFileIndex + 1} von {files.length}: {files[currentFileIndex]?.name}
            </div>
          )}
          <ZeoChat
            events={events}
            zeoText={zeoText}
            isStreaming={isAnalysing}
            fortschritt={fertig ? 100 : 50}
            error={error}
          />
        </>
      )}

      {/* Ergebnisse */}
      {fertig && ergebnisse.length > 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-slate-200 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              Ergebnis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {ergebnisse.map((erg, i) => (
              <div key={i} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <div className="text-sm font-medium text-slate-200 mb-2">{erg.datei}</div>
                {erg.analyse && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {erg.analyse.klient_name && (
                        <div className="text-slate-400">Klient: <span className="text-slate-200">{erg.analyse.klient_name}</span></div>
                      )}
                      {erg.analyse.ordner && (
                        <div className="text-slate-400">Ordner: <span className="text-slate-200">{erg.analyse.ordner}</span></div>
                      )}
                      {erg.analyse.absender && (
                        <div className="text-slate-400">Absender: <span className="text-slate-200">{erg.analyse.absender}</span></div>
                      )}
                      {erg.analyse.dokumenttyp && (
                        <div className="text-slate-400">Typ: <span className="text-slate-200">{erg.analyse.dokumenttyp}</span></div>
                      )}
                    </div>

                    {/* Bescheid-Details */}
                    {erg.analyse.bescheid?.ist_bescheid && (
                      <div className="bg-emerald-950/30 rounded-lg p-3 border border-emerald-800/50" data-testid="bescheid-ergebnis">
                        <div className="text-xs font-medium text-emerald-400 mb-2">Bescheid erkannt</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {erg.analyse.bescheid.leistungsart && (
                            <div className="text-slate-400">Leistung: <span className="text-emerald-300">{erg.analyse.bescheid.leistungsart}</span></div>
                          )}
                          {erg.analyse.bescheid.betrag_monatlich > 0 && (
                            <div className="text-slate-400">Betrag: <span className="text-emerald-300 font-bold">{erg.analyse.bescheid.betrag_monatlich?.toFixed(2)} EUR/mtl.</span></div>
                          )}
                          {erg.analyse.bescheid.laufzeit_von && (
                            <div className="text-slate-400">Von: <span className="text-slate-200">{erg.analyse.bescheid.laufzeit_von}</span></div>
                          )}
                          {erg.analyse.bescheid.laufzeit_bis && (
                            <div className="text-slate-400">Bis: <span className="text-rose-300 font-medium">{erg.analyse.bescheid.laufzeit_bis}</span></div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Autonome Aktionen */}
                    {events.filter(e => ['vorgang_erstellt', 'bescheid_verknuepft', 'betrag_aktualisiert', 'aufgabe_erledigt', 'weiterbewilligung_aufgabe', 'aufgabe_erstellt', 'frist_erstellt'].includes(e.type)).length > 0 && (
                      <div className="bg-blue-950/30 rounded-lg p-3 border border-blue-800/50" data-testid="autonome-aktionen">
                        <div className="text-xs font-medium text-blue-400 mb-2">Zeo hat autonom erledigt:</div>
                        <div className="space-y-1">
                          {events.filter(e => ['vorgang_erstellt', 'bescheid_verknuepft', 'betrag_aktualisiert', 'aufgabe_erledigt', 'weiterbewilligung_aufgabe', 'aufgabe_erstellt', 'frist_erstellt'].includes(e.type)).map((evt, j) => (
                            <div key={j} className="flex items-center gap-2 text-xs text-slate-300">
                              {EVENT_ICONS[evt.type] || <CheckCircle className="w-3 h-3 text-slate-500" />}
                              <StatusEvent event={evt} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Rückfrage */}
                    {erg.analyse.rueckfrage && (
                      <div className="bg-yellow-950/30 rounded-lg p-3 border border-yellow-800/50" data-testid="rueckfrage-ergebnis">
                        <div className="text-xs font-medium text-yellow-400 mb-1">Rückfrage:</div>
                        <div className="text-sm text-yellow-200">{erg.analyse.rueckfrage}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="border-slate-700 text-slate-300"
                onClick={() => {
                  setFiles([]);
                  setEvents([]);
                  setZeoText('');
                  setError(null);
                  setFertig(false);
                  setErgebnisse([]);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Weitere Post erfassen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
